import { motion } from "framer-motion";
import { Database, Server, Cloud, ArrowRight, ArrowDown } from "lucide-react";

const SOURCES = [
    { label: "Core Banking", icon: Server },
    { label: "ERP / CRM", icon: Database },
    { label: "Utility & Supplier Feeds", icon: Cloud }
];

const OUTPUTS = ["Credit Committee", "Procurement Ops", "Supervisory Review"];

export default function OverlayDiagram() {
    return (
        <div className="mt-16 bg-white border border-navy/10 p-6 lg:p-10" data-testid="overlay-diagram">
            <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-px bg-gold"></div>
                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold">
                    How Carbonis sits above your stack
                </span>
            </div>

            {/* Layer 1 — Sources */}
            <div className="grid grid-cols-3 gap-3 lg:gap-6">
                {SOURCES.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <motion.div
                            key={s.label}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            className="border border-navy/15 bg-paper p-4 lg:p-5 flex flex-col gap-2"
                            data-testid={`overlay-source-${i}`}
                        >
                            <Icon size={18} strokeWidth={1.35} className="text-navy/60" />
                            <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-navy/60">
                                {s.label}
                            </div>
                            <div className="font-mono text-[9px] tracking-[0.12em] uppercase text-navy/35">
                                Read-only · No migration
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {/* Connector — three arrows into center */}
            <div className="relative h-16 flex items-center justify-center">
                <div className="absolute inset-0 flex items-start justify-around pt-1">
                    {SOURCES.map((_, i) => (
                        <ArrowDown key={i} size={18} strokeWidth={1.25} className="text-navy/30" />
                    ))}
                </div>
            </div>

            {/* Layer 2 — Carbonis */}
            <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="relative bg-navy text-paper p-6 lg:p-8 border border-navy"
                data-testid="overlay-carbonis"
            >
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold mb-2">
                            Carbonis · Intelligence Overlay
                        </div>
                        <div className="font-serif text-2xl lg:text-3xl leading-tight tracking-[-0.01em]">
                            Identify <span className="text-gold">→</span> Quantify <span className="text-gold">→</span> Mitigate <span className="text-gold">→</span> Protect
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {["CRX™ Score", "Confidence-Weighted", "Regulator-Ready", "Audit Trail"].map((t) => (
                            <span
                                key={t}
                                className="font-mono text-[9.5px] tracking-[0.16em] uppercase text-paper/75 border border-white/20 px-2.5 py-1"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Connector down */}
            <div className="relative h-16 flex items-center justify-center">
                <ArrowDown size={20} strokeWidth={1.25} className="text-navy/30" />
            </div>

            {/* Layer 3 — Decisions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-6">
                {OUTPUTS.map((o, i) => (
                    <motion.div
                        key={o}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.6, delay: 0.6 + i * 0.08 }}
                        className="border border-gold/60 bg-paper p-4 lg:p-5 flex items-center justify-between"
                        data-testid={`overlay-decision-${i}`}
                    >
                        <div className="flex flex-col gap-1">
                            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-gold">Decision</span>
                            <span className="font-serif text-lg text-navy leading-tight">{o}</span>
                        </div>
                        <ArrowRight size={16} strokeWidth={1.35} className="text-gold" />
                    </motion.div>
                ))}
            </div>

            {/* Footer note */}
            <div className="mt-6 pt-4 border-t border-navy/10 flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-gold"></span>
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/55">
                    Zero core integration · Zero data migration · Zero workflow disruption
                </span>
            </div>
        </div>
    );
}
